import {
  ConnectorDataCollection,
  ConnectorMethodImplementationType,
  ConnectorSpec,
} from '@integration-app/sdk'
import { useState } from 'react'
import classes from 'routes/Connectors/Connector/components/WorkWithCollections/workWithConnections.module.css'
import { Hgroup } from 'routes/components/Hgroup'
import { DOCS_LINK } from 'routes/constants'

const INITIAL_COLLECTIONS_COUNT = 6

function collectionSupportsOperation(
  collection: ConnectorDataCollection,
  operationKey: string,
) {
  const operation = collection.methods?.[operationKey]
  return (
    operation &&
    operation.implementationType !==
      ConnectorMethodImplementationType.notSupported
  )
}

const operations = [
  {
    key: 'list',
    name: 'List',
    check: (collection: ConnectorDataCollection) =>
      collectionSupportsOperation(collection, 'list'),
  },
  {
    key: 'findById',
    name: 'Find by ID',
    check: (collection: ConnectorDataCollection) =>
      collectionSupportsOperation(collection, 'findById'),
  },
  {
    key: 'search',
    name: 'Search',
    isOptional: true,
    check: (collection: ConnectorDataCollection) =>
      collectionSupportsOperation(collection, 'search'),
  },
  {
    key: 'match',
    name: 'Match',
    isOptional: true,
    check: (collection: ConnectorDataCollection) =>
      collectionSupportsOperation(collection, 'match'),
  },
  {
    key: 'create',
    name: 'Create',
    check: (collection: ConnectorDataCollection) =>
      collectionSupportsOperation(collection, 'create'),
  },
  {
    key: 'update',
    name: 'Update',
    check: (collection: ConnectorDataCollection) =>
      collectionSupportsOperation(collection, 'update'),
  },
  {
    key: 'delete',
    name: 'Delete',
    check: (collection: ConnectorDataCollection) =>
      collectionSupportsOperation(collection, 'delete'),
  },
  {
    name: 'Custom Fields',
    isOptional: true,
    check: (collection: ConnectorDataCollection) => collection.customFields,
  },
]

export function WorkWithCollections({
  connector,
  collections,
}: {
  connector: ConnectorSpec
  collections: ConnectorDataCollection[]
}) {
  const [showAll, setShowAll] = useState(false)
  const toggleShowAll = () => setShowAll(!showAll)

  if (!collections.length) {
    return null
  }

  const operationsToShow = operations.filter(
    (o) => !o.isOptional || collections.find((c) => o.check(c)),
  )

  const sortedCollections = collections.map((collection) => ({
    ...collection,
    supportedOps: operationsToShow.filter((o) => o.check(collection)).length,
  }))

  sortedCollections.sort((a, b) => {
    if (a.supportedOps === b.supportedOps) return 0
    if (a.supportedOps === 0) return 1
    if (b.supportedOps === 0) return -1
    return a.supportedOps < b.supportedOps ? 1 : -1
  })

  const showCollections = showAll
    ? sortedCollections
    : sortedCollections.slice(0, INITIAL_COLLECTIONS_COUNT)

  return (
    <div className={classes.wrapper}>
      <Hgroup>
        <h2>Work with {connector.name} data collections</h2>
        <p>
          Read and write data, get custom fields schema, and react to changes in{' '}
          {connector.name} data.
        </p>
        <p>
          <a target='_blank' href={`${DOCS_LINK}/connectors/data`}>
            Read more about working with data collections
          </a>
        </p>
      </Hgroup>
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead className={classes.tableHead}>
            <tr>
              <th className={classes.tableItem} />
              {operationsToShow.map((operation) => (
                <th className={classes.tableItem} key={operation.key}>
                  {operation.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={classes.tableBody}>
            {showCollections.map((collection) => (
              <tr className={classes.tableRow} key={collection.key}>
                <td className={classes.tableItem}>{collection.name}</td>
                {operationsToShow.map((operation) => (
                  <td className={classes.tableItem} key={operation.key}>
                    {operation.check(collection) ? '✅' : '➖'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {collections.length > INITIAL_COLLECTIONS_COUNT && (
        <div className={classes.buttonWrapper}>
          <button
            type={'button'}
            className={'button button__rounded button__bordered'}
            onClick={toggleShowAll}
          >
            {showAll
              ? 'Collapse'
              : `Show all ${collections.length} collections`}
          </button>
        </div>
      )}
    </div>
  )
}
